import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import en from './en.json';
import {
  FALLBACK_LANGUAGES,
  LANG_STORAGE_KEY,
  SOURCE_LANG,
  parseLanguageList,
  parseLocaleFile,
  readLang,
  type LanguageOption,
} from '../lib/locale';

/**
 * i18next setup. English is bundled (`./en.json`, the source every key is typed from); any other language is
 * fetched from `/locales/<code>.json` (nginx serves JSON with `no-cache`, so an edited file is live after the
 * next deploy). A missing word falls back to English, never to a raw key.
 */
const storage = (() => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();

// The language actually shown: a missing locale file resolves to English, and <html lang> must say so.
const setHtmlLang = (lng: string) => {
  document.documentElement.lang = i18n.resolvedLanguage ?? lng;
};
i18n.on('languageChanged', setHtmlLang);

/** Resolves once the chosen language is loaded (or has failed and fallen back) — render after it, no English flash. */
export const i18nReady = i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: readLang(storage),
    fallbackLng: SOURCE_LANG,
    resources: { [SOURCE_LANG]: { translation: en } },
    partialBundledLanguages: true, // English from the bundle, the rest through the backend
    load: 'currentOnly',
    interpolation: { escapeValue: false }, // React escapes
    returnEmptyString: false,
    // One try: a failed locale fetch falls back to English instead of retrying for ~11 s behind a blank page.
    maxRetries: 0,
    // Render with whatever is loaded (English at worst) — messaging.tsx stops waiting after a few seconds.
    react: { useSuspense: false },
    backend: {
      loadPath: '/locales/{{lng}}.json',
      parse: (data: string) => parseLocaleFile(data),
    },
  })
  .catch(() => undefined);

export async function setLanguage(code: string): Promise<void> {
  try {
    storage?.setItem(LANG_STORAGE_KEY, code);
  } catch {
    // not remembered, but still switch for this visit
  }
  await i18n.changeLanguage(code);
}

/** Languages offered in the switch, from `/locales/index.json`; the shipped two when it cannot be read. */
export async function loadLanguageList(): Promise<LanguageOption[]> {
  try {
    const res = await fetch('/locales/index.json', { cache: 'no-cache' });
    if (!res.ok) return FALLBACK_LANGUAGES;
    // A missing file comes back as the SPA's HTML: json() throws and the shipped list is used.
    return parseLanguageList(await res.json()) ?? FALLBACK_LANGUAGES;
  } catch {
    return FALLBACK_LANGUAGES;
  }
}

export default i18n;
